import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import Input from "@/components/ui/input";
import { LeadData } from "@/pages/leads/types";
import IconContainer from "@/components/ui/icon-container";
import { ArrMrr, ChevronDown } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import SingleSelect from "@/components/controls/SingleSelect";
import NoLicensesEmptyState from "@/components/controls/NoLicensesEmptyState";
import { createDataMapById } from "@/utils/datamaps";
import { compact, isNumber, map } from "lodash";

interface LicenseTypeItem {
  id: string;
  name: string;
}

interface LicenseFieldsProps {
  lead: LeadData;
  onUpdateFields: (licenseId: string | null, numberOfLicenses: number) => void;
  licenseTypes: LicenseTypeItem[];
  disabled?: boolean;
  disabledTooltipText?: string;
}

const LicenseFieldsEditor: React.FC<LicenseFieldsProps> = ({
  lead,
  onUpdateFields,
  licenseTypes,
  disabled = false,
  disabledTooltipText
}) => {
  const { t } = useTranslation();
  const { licenseId, numberOfLicenses } = lead;
  const [isOpen, setIsOpen] = useState(false);
  const [tempLicenseType, setTempLicenseType] = useState(licenseId);
  const [tempNumberOfLicenses, setTempNumberOfLicenses] =
    useState(numberOfLicenses);

  useEffect(() => {
    if (isOpen) {
      setTempLicenseType(licenseId);
      setTempNumberOfLicenses(numberOfLicenses);
    }
  }, [isOpen, licenseId, numberOfLicenses]);

  // Close if the field locks while open, so it doesn't pop back open on unlock
  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const handleApply = () => {
    onUpdateFields(tempLicenseType, tempNumberOfLicenses ?? 0);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (open && disabled) return;
    setIsOpen(open);
    if (!open) {
      setTempLicenseType(licenseId);
      setTempNumberOfLicenses(numberOfLicenses);
    }
  };

  const licenseTypesDataMap = useMemo(
    () => createDataMapById(licenseTypes),
    [licenseTypes]
  );

  const hasDisabledTooltip = disabled && !!disabledTooltipText;

  const trigger = (
    <div
      aria-disabled={disabled || undefined}
      // Focusable when locked so keyboard users can reach the tooltip
      role={hasDisabledTooltip ? "button" : undefined}
      // Without a count there's no visible text to name the button
      aria-label={
        hasDisabledTooltip && !isNumber(numberOfLicenses)
          ? disabledTooltipText
          : undefined
      }
      tabIndex={hasDisabledTooltip ? 0 : undefined}
      className={cn(
        "group flex items-center gap-1 h-auto border border-transparent p-2 rounded-lg w-full justify-between transition-all duration-200 ease-in-out",
        disabled ? "cursor-default" : "cursor-pointer",
        isOpen && "border-primary bg-surface-focus-selected",
        !isOpen &&
          !disabled &&
          "hover:bg-surface-hover-strong border border-transparent"
      )}
    >
      <span className={cn("contents", disabled && "[&_*]:!text-body-disabled")}>
        <ArrMrr />
      </span>
      {isNumber(numberOfLicenses) ? (
        <Text variant="body" className={cn(disabled && "!text-body-disabled")}>
          {compact([
            String(numberOfLicenses),
            licenseId && licenseTypesDataMap[licenseId]?.name,
            t("leads.leadView.licenses")
          ]).join(" ")}
        </Text>
      ) : (
        <IconContainer
          size="sm"
          color="secondary"
          className={cn(
            "transition-opacity duration-200 ease-out opacity-0",
            !disabled && "group-hover:opacity-100",
            isOpen && "opacity-100"
          )}
        >
          <ChevronDown />
        </IconContainer>
      )}
    </div>
  );

  return (
    <Popover open={isOpen && !disabled} onOpenChange={handleOpenChange}>
      {hasDisabledTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>{disabledTooltipText}</TooltipContent>
        </Tooltip>
      ) : disabled ? (
        trigger
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent
        align="start"
        padding={16}
        className="flex flex-col gap-4 w-fit"
      >
        <div className="flex gap-4">
          <div className="flex gap-2 flex-col">
            <Text variant="bodySecondary">
              {t("leads.createModal.licenseId")}
            </Text>
            <SingleSelect
              selectedValue={tempLicenseType ?? undefined}
              options={map(licenseTypes, (license) => ({
                id: license?.id,
                displayName: license?.name
              }))}
              onSelection={(value) => setTempLicenseType(value as string)}
              fieldName="licenseId"
              className="w-28 min-w-28"
              variant="form"
              popOverHeader={
                licenseTypes.length === 0 ? (
                  <NoLicensesEmptyState
                    labels={{
                      title: t("common.noLicensesTitle"),
                      description: t("common.noLicensesDescription")
                    }}
                  />
                ) : undefined
              }
            />
          </div>
          <div className="flex gap-2 flex-col">
            <Text variant="bodySecondary">
              {t("leads.createModal.numOfLicenses")}
            </Text>
            <Input
              type="number"
              min="0"
              value={tempNumberOfLicenses ?? 0}
              onChange={(e) => setTempNumberOfLicenses(Number(e.target.value))}
              className="w-full border-widget"
            />
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <Button variant="secondary" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button onClick={handleApply}>{t("apply")}</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LicenseFieldsEditor;
